import React, { useState, useRef, useCallback } from "react";
import { BrPageContext, BrComponentContext } from "@bloomreach/react-sdk";
import SavedProductsCard from "./SavedProductsCard";
import ShareProductsModal from "./ShareProductsModal";
import { useSavedProducts } from "./hooks/useSavedProducts";
import { DISPLAYMODE } from "../Search/SearchToolbar/DisplayMode";
import DisplayMode from "../Search/SearchToolbar/DisplayMode";
import { SearchToolbarContext } from "../Search/SearchToolbar/context";
import { positionModal } from "../Search/searchUtils";
import "../Search/SearchToolbar/DisplayMode.scss";
import "./SavedProductsController.scss";
import ErrorComponent from "../Common/ErrorComponent/ErrorComponent";
import Title from "../Global/Title/Title";
import { HEADING_LEVELS } from "../Global/GlobalConstants";

// Feature flags
const ENABLE_HOVER_MODE = true; // Set to true to enable grid compact hover mode

function SavedProductsController() {
  const page = React.useContext(BrPageContext);
  const component = React.useContext(BrComponentContext);
  const { document: documentRef, servletUrl, shareThisUrl } = component.getModels();

  const [modalOpen, setModalOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState(ENABLE_HOVER_MODE ? DISPLAYMODE.GRIDCOMPACT : DISPLAYMODE.GRIDEXPANDED);
  const modalContainerRef = useRef(null);

  // Use custom hook for products management
  const {
    products,
    numberToShow,
    productCount,
    loadMore,
    clearList,
    printList,
  } = useSavedProducts(servletUrl);

  const toggleModal = useCallback(() => {
    setModalOpen(prev => !prev);
  }, []);

  try {
    const document = page.getContent(documentRef);
    if (!document) return null;

    const { productHeight, productWidth } = component.getParameters();

    const {
      title = null,
      discontinuedProductLabel = null,
      discontinuedProductDescription = null,
      itemCountLabel = null,
      printListLabel = null,
      clearListLabel = null,
      shareThisLabel = "Share This",
    } = document.getData();

    const labelsOutput = {
      titleLabel: title,
      discontinuedLabel: discontinuedProductLabel,
      discontinuedDescription: discontinuedProductDescription.value,
      itemCountLabel: itemCountLabel,
      printListLabel: printListLabel,
      clearListLabel: clearListLabel,
      shareThisLabel: shareThisLabel,
    };

    const getTitle = () => {
      let titleOutput = title;
      if (titleOutput !== null) {
        titleOutput = titleOutput.replace("{}", productCount);
      }
      return titleOutput;
    };

    return (
      <div className="saved-products-controller">
        <div className="saved-products-controller__wrapper">
          {/* Header Section */}
          <div className="saved-products-controller__header-wrapper">
            <Title
              defaultLevel={HEADING_LEVELS.ONE}
              className="saved-products-controller__header-text"
            >
              {getTitle()}
            </Title>

            {/* Print and Clear List CTAs */}
            <div className="saved-products-controller__header-cta-wrapper">
              {products && productCount > 0 && (
                <button className="saved-products-controller__header-cta" onClick={toggleModal}>
                  <span className="saved-products-controller__header-cta-icon darticon-email"></span>
                  <b className="saved-products-controller__header-cta-label">
                    {labelsOutput["shareThisLabel"]}
                  </b>
                </button>
              )}
              <button className="saved-products-controller__header-cta" onClick={printList}>
                <span className="saved-products-controller__header-cta-icon darticon-fax"></span>
                <b className="saved-products-controller__header-cta-label">
                  {labelsOutput["printListLabel"]}
                </b>
              </button>
              <button className="saved-products-controller__header-cta" onClick={clearList}>
                <span className="saved-products-controller__header-cta-icon darticon-close"></span>
                <b className="saved-products-controller__header-cta-label">
                  {labelsOutput["clearListLabel"]}
                </b>
              </button>
            </div>
          </div>

          {/* Display Mode Toggle */}
          {products && productCount > 0 && (
            <SearchToolbarContext.Provider
              value={{
                displaymode: {
                  option1: ENABLE_HOVER_MODE ? { 
                    value: DISPLAYMODE.GRIDCOMPACT, 
                    label: window.globalProperties["displayModeGridCompactLabel"] || "Grid Compact", 
                    className: "icon-simple-view" 
                  } : null,
                  option2: { 
                    value: DISPLAYMODE.GRIDEXPANDED, 
                    label: window.globalProperties["displayModeGridExpandedLabel"] || "Grid Expanded", 
                    className: "icon-details-view" 
                  },
                  option3: { 
                    value: DISPLAYMODE.LIST, 
                    label: window.globalProperties["displayModeListViewLabel"] || "List View", 
                    className: "icon-list-view" 
                  },
                  defaultSelected: ENABLE_HOVER_MODE ? DISPLAYMODE.GRIDCOMPACT : DISPLAYMODE.GRIDEXPANDED,
                  name: "savedProductsDisplayMode",
                  label: window.globalProperties["displayModeToggleLabel"] || "Display Mode",
                  ariaLabel: window.globalProperties["displayModeToggleAriaLabel"] || "Display Mode Toggle",
                  onChange: setDisplayMode,
                  hidden: false,
                },
              }}
            >
              <div className="saved-products-controller__display-mode-wrapper">
                <DisplayMode />
              </div>
            </SearchToolbarContext.Provider>
          )}

          <div id="modal-container" className="modal-container" ref={modalContainerRef}></div>

          {/* Products List */}
          {products && productCount > 0 && (
            <div className="saved-products-controller__products-wrapper">
              <ul className={`saved-products-controller__products-list ${displayMode}`}>
                {products.slice(0, numberToShow).map((item, index) => (
                  <li className="saved-products-controller__product" key={index}>
                    <SavedProductsCard
                      product={item}
                      labels={labelsOutput}
                      productImageHeight={productHeight}
                      productImageWidth={productWidth}
                      displayMode={displayMode}
                      modalContainerRef={modalContainerRef}
                      positionModal={positionModal}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Load More CTA */}
          {products && productCount > numberToShow && (
            <div className="saved-products-controller__load-more-wrapper">
              <button className="btn btn--secondary" onClick={loadMore}>
                {window.globalProperties["loadMoreLabel"] || "Load More"}
              </button>
            </div>
          )}
        </div>
        
        <ShareProductsModal
          show={modalOpen}
          onClose={toggleModal}
          products={products}
          shareThisUrl={shareThisUrl}
        />
      </div>
    );
  } catch (e) {
    console.error("SavedProducts error", e);
    return <ErrorComponent error={e} componentName={"SavedProducts"} />;
  }
}

export default SavedProductsController;
